import { roundAmount } from '../../../shared/round-amount';

export interface ProjectListItem {
  uid: string;
  name: string;
  description: string;
  categoryId: number | null;
  categoryName: string;
  riskLevelId: number | null;
  riskLevelName: string;
  startDate: string;
  endDate: string | null;
  generalBudget: number;
  // Financiación conseguida (financiaciones del proyecto); la lista de Asociaciones la pinta como barra frente al presupuesto.
  financingTotal?: number;
  // Total justificado (gastos, nóminas e inversión); solo viene con includeJustified.
  justifiedTotal?: number;
  isActive: boolean;
  isNonprofitProject: boolean;
  investorsCount: number;
  investedCapital: number;
  documentsCount: number;
  notes: string;
}

export interface ProjectListSummary {
  count: number;
  activeCount: number;
  totalBudget: number;
  investedCapital: number;
}

export interface ProjectListFilter {
  page: number;
  pageSize: number;
  search?: string;
  sortBy?: string;
  sortDir?: string;
  categoryIds?: number[];
  riskLevelIds?: number[];
  active?: boolean | null;
  // Calcula el justificado de cada proyecto (lista de Asociaciones): más pesado, solo si se pide.
  includeJustified?: boolean;
}

export interface ProjectListResult {
  items: ProjectListItem[];
  total: number;
  summary: ProjectListSummary;
}

export const EMPTY_PROJECT_SUMMARY: ProjectListSummary = {
  count: 0,
  activeCount: 0,
  totalBudget: 0,
  investedCapital: 0,
};

export interface ProjectRow extends ProjectListItem {
  __expanded?: boolean;
}

export interface ProjectRecord {
  uid?: string;
  name: string;
  description: string;
  categoryId: number | null;
  riskLevelId: number | null;
  startDate: string;
  endDate: string | null;
  generalBudget: number;
  isActive: boolean;
  countryCode: string;
  provinceCode: string;
  municipalityCode: string;
  notes: string;
  // Solo asociaciones/fundaciones (subvenciones)
  isNonprofitProject: boolean;
  projectType: string;
  line: string;
  subline: string;
  resolution: string;
  managerEmployeeUids: string[];
  managers?: ProjectManager[];
  // Reparto DESEADO del presupuesto inicial por categoría (asociaciones/fundaciones); luego se busca cubrirlo con financiaciones.
  categoryBudgets?: { category: string; amount: number }[];
}

export interface ProjectManager {
  uid: string;
  name: string;
}

export interface ProjectManagerHistoryRow {
  employeeUid: string;
  name: string;
  startDate: string;
  endDate: string | null;
}

export interface ProjectAlertDocument {
  uid: string;
  number: string;
  date: string | null;
  amount: number;
}

export interface ProjectAlert {
  type: string;
  message: string;
  documents: ProjectAlertDocument[];
}

// Últimos movimientos que componen lo justificado de una categoría (gastos, nóminas imputadas, inversiones)
export interface ProjectCategoryMovement {
  kind: 'expense' | 'payroll' | 'investment';
  date: string | null;
  counterparty: string;
  description: string;
  amount: number;
}

export interface ProjectCategoryMovements {
  category: string;
  total: number;
  sum: number;
  items: ProjectCategoryMovement[];
}

export interface ProjectNonprofitExpenseCategory {
  name: string;
  total: number;
  // Fase 9 (Asociaciones/Fundaciones): presupuesto asignado (suma de financiaciones) y % justificado.
  // Financiado: suma de lo que aportan las financiaciones a esta categoría (antes se llamaba "presupuesto").
  budget?: number | null;
  // Presupuestado: reparto deseado del presupuesto inicial del proyecto (solo en el informe del proyecto).
  planned?: number | null;
  percent?: number | null;
}

export interface ProjectNonprofitExpenseReport {
  uid: string;
  total: number;
  categories: ProjectNonprofitExpenseCategory[];
  // Base del % de justificación y la parte de la financiación sin repartir por categorías.
  financingTotal?: number;
  unassignedBudget?: number;
}

export interface ProjectDetailInvestor {
  uid: string;
  name: string;
  nif: string;
  initialCapital: number;
}

export interface ProjectDetailDocument {
  type: string;
  label: string;
  count: number;
  total: number;
}

export interface ProjectDetail {
  uid: string;
  investors: ProjectDetailInvestor[];
  investedCapital: number;
  documents: ProjectDetailDocument[];
}

export interface ProjectInvestment {
  uid?: string;
  projectUid: string;
  name: string;
  nif: string;
  initialCapital: number;
}

export interface ProjectReportExpense {
  name: string;
  total: number;
}

export interface ProjectReportInvestor {
  name: string;
  initialCapital: number;
  share: number;
}

export interface ProjectReport {
  uid: string;
  name: string;
  companyName: string;
  startDate: string;
  endDate: string | null;
  daysElapsed: number;
  daysRemaining: number | null;
  totalDays: number | null;
  delayDays: number;
  generalBudget: number;
  totalExpenses: number;
  totalIncomes: number;
  difference: number;
  differenceBudget: number;
  delayCost: number;
  profitability: number;
  investorsCount: number;
  expensesByType: ProjectReportExpense[];
  investors: ProjectReportInvestor[];
}

export type ProjectDocumentType = 'invoice' | 'ticket';

export interface ProjectExportFile {
  blob: Blob;
  fileName: string;
}

export function toProjectRow(item: ProjectListItem, expanded: boolean): ProjectRow {
  return {
    ...item,
    name: item.name || '',
    description: item.description || '',
    categoryName: item.categoryName || '',
    riskLevelName: item.riskLevelName || '',
    generalBudget: Number(item.generalBudget) || 0,
    investorsCount: Number(item.investorsCount) || 0,
    investedCapital: Number(item.investedCapital) || 0,
    documentsCount: Number(item.documentsCount) || 0,
    isActive: !!item.isActive,
    isNonprofitProject: !!item.isNonprofitProject,
    __expanded: expanded,
  };
}

export function roundMoney(value: number): number {
  return roundAmount(value);
}

export function formatProjectDate(value: any): string {
  if (!value) return '';
  const text = String(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  const date = new Date(text);
  if (isNaN(date.getTime()) || date.getFullYear() <= 1) return '';
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function toIsoDate(value: any): string | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return null;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function fromIsoDate(value: any): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (match) {
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return date.getFullYear() <= 1 ? null : date;
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}
